import { useState } from 'react';
import { Link } from 'react-router-dom';
import { spotlightCategories, spotlightBusinesses } from '../../../mocks/businessSpotlight';
import { homepageSpotlightBusinesses } from '../../../mocks/sponsoredContent';

const badgeColors: Record<string, string> = {
  teal: 'bg-teal-100 text-teal-700',
  amber: 'bg-amber-100 text-amber-700',
  rose: 'bg-rose-100 text-rose-700',
  orange: 'bg-orange-100 text-orange-700',
  violet: 'bg-violet-100 text-violet-700',
  emerald: 'bg-emerald-100 text-emerald-700',
};

function StarRating({ rating }: { rating: number }) {
  return (
    <div className="flex items-center gap-0.5">
      {Array.from({ length: 5 }).map((_, i) => (
        <i
          key={i}
          className={`text-sm ${
            i < rating ? 'ri-star-fill text-amber-400' : 'ri-star-line text-gray-300'
          }`}
        />
      ))}
    </div>
  );
}

function BusinessCard({
  business,
  isLarge,
}: {
  business: typeof spotlightBusinesses[0];
  isLarge: boolean;
}) {
  const [showReviews, setShowReviews] = useState(false);

  if (isLarge) {
    return (
      <article className="group bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-500 border border-gray-100">
        <div className="grid md:grid-cols-2">
          <div className="relative h-72 md:h-full min-h-[320px] overflow-hidden">
            <img
              src={business.image}
              alt={`${business.name} - top rated ${business.category} business in Roseville California`}
              title={`${business.name} Roseville CA`}
              className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent md:bg-gradient-to-r md:from-transparent md:via-transparent md:to-black/10" />
            <div className="absolute top-4 left-4">
              <span
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold ${
                  badgeColors[business.badgeColor] || badgeColors.teal
                }`}
              >
                <i className="ri-award-fill text-xs" />
                {business.badge}
              </span>
            </div>
            <div className="absolute bottom-4 left-4 md:hidden">
              <h3 className="text-xl font-bold text-white drop-shadow-lg">{business.name}</h3>
              <p className="text-white/80 text-sm">{business.tagline}</p>
            </div>
          </div>

          <div className="p-7 flex flex-col">
            <div className="hidden md:block mb-1">
              <span
                className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold ${
                  badgeColors[business.badgeColor] || badgeColors.teal
                } mb-3`}
              >
                <i className="ri-award-fill text-xs" />
                {business.badge}
              </span>
              <h3 className="text-2xl font-bold text-gray-900 mb-1">{business.name}</h3>
              <p className="text-teal-600 text-sm font-medium">{business.tagline}</p>
            </div>

            <div className="flex items-center gap-3 mt-3 mb-4">
              <div className="flex items-center gap-1.5">
                <span className="text-lg font-bold text-gray-900">{business.rating}</span>
                <StarRating rating={Math.round(business.rating)} />
              </div>
              <span className="text-sm text-gray-500">({business.reviewCount} reviews)</span>
              <span className="text-sm text-gray-400 font-medium">{business.priceRange}</span>
            </div>

            <p className="text-sm text-gray-600 leading-relaxed mb-4">{business.description}</p>

            <div className="flex flex-wrap gap-2 mb-5">
              {business.highlights.map((h) => (
                <span
                  key={h}
                  className="px-3 py-1 bg-gray-50 text-gray-600 text-xs font-medium rounded-full border border-gray-100"
                >
                  {h}
                </span>
              ))}
            </div>

            {/* Review Toggle */}
            <button
              onClick={() => setShowReviews(!showReviews)}
              className="flex items-center gap-2 text-sm font-semibold text-teal-600 hover:text-teal-700 transition-colors cursor-pointer mb-4"
            >
              <i className="ri-chat-quote-line" />
              {showReviews ? 'Hide Reviews' : 'Read Reviews'}
              <i
                className={`ri-arrow-${showReviews ? 'up' : 'down'}-s-line text-base transition-transform`}
              />
            </button>

            <div
              className={`overflow-hidden transition-all duration-400 ease-in-out ${
                showReviews ? 'max-h-[300px] opacity-100 mb-4' : 'max-h-0 opacity-0'
              }`}
            >
              <div className="space-y-3">
                {business.reviews.map((review, idx) => (
                  <div
                    key={idx}
                    className="bg-gray-50 rounded-xl p-4 border border-gray-100"
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <div className="w-7 h-7 bg-teal-100 rounded-full flex items-center justify-center">
                          <span className="text-xs font-bold text-teal-700">
                            {review.author[0]}
                          </span>
                        </div>
                        <span className="text-sm font-semibold text-gray-800">
                          {review.author}
                        </span>
                      </div>
                      <span className="text-xs text-gray-400">{review.date}</span>
                    </div>
                    <StarRating rating={review.rating} />
                    <p className="text-sm text-gray-600 mt-2 leading-relaxed">
                      &ldquo;{review.text}&rdquo;
                    </p>
                  </div>
                ))}
              </div>
            </div>

            <div className="mt-auto flex items-center gap-4 pt-4 border-t border-gray-100">
              <div className="flex items-center gap-1.5 text-xs text-gray-500">
                <i className="ri-map-pin-2-line text-teal-500" />
                <span className="truncate max-w-[180px]">{business.address}</span>
              </div>
              <div className="flex items-center gap-1.5 text-xs text-gray-500">
                <i className="ri-time-line text-teal-500" />
                <span>{business.hours}</span>
              </div>
            </div>

            <div className="flex gap-3 mt-4">
              <a
                href={`tel:${business.phone}`}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-teal-600 text-white rounded-lg text-sm font-semibold hover:bg-teal-700 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-phone-line" />
                Call Now
              </a>
              <a
                href={`https://maps.google.com/?q=${encodeURIComponent(
                  business.address
                )}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 px-4 py-2.5 bg-gray-100 text-gray-700 rounded-lg text-sm font-semibold hover:bg-gray-200 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-direction-line" />
                Directions
              </a>
            </div>
          </div>
        </div>
      </article>
    );
  }

  return (
    <article className="group bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-500 border border-gray-100 flex flex-col">
      <div className="relative h-52 overflow-hidden">
        <img
          src={business.image}
          alt={`${business.name} - top rated ${business.category} business in Roseville California`}
          title={`${business.name} Roseville CA`}
          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />
        <div className="absolute top-3 left-3">
          <span
            className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold ${
              badgeColors[business.badgeColor] || badgeColors.teal
            }`}
          >
            <i className="ri-award-fill text-xs" />
            {business.badge}
          </span>
        </div>
        <div className="absolute bottom-3 right-3">
          <span className="px-2 py-1 bg-white/90 backdrop-blur-sm text-gray-700 text-xs font-semibold rounded-md">
            {business.priceRange}
          </span>
        </div>
      </div>

      <div className="p-5 flex flex-col flex-1">
        <h3 className="text-lg font-bold text-gray-900 mb-0.5">{business.name}</h3>
        <p className="text-teal-600 text-xs font-medium mb-3">{business.tagline}</p>

        <div className="flex items-center gap-2 mb-3">
          <span className="text-sm font-bold text-gray-900">{business.rating}</span>
          <StarRating rating={Math.round(business.rating)} />
          <span className="text-xs text-gray-400">({business.reviewCount})</span>
        </div>

        <p className="text-sm text-gray-600 leading-relaxed mb-4 line-clamp-2">{business.description}</p>

        <div className="flex flex-wrap gap-1.5 mb-4">
          {business.highlights.map((h) => (
            <span
              key={h}
              className="px-2.5 py-0.5 bg-gray-50 text-gray-500 text-xs font-medium rounded-full border border-gray-100"
            >
              {h}
            </span>
          ))}
        </div>

        {/* Top Review Preview */}
        <div className="bg-gray-50 rounded-xl p-3.5 border border-gray-100 mb-4">
          <div className="flex items-center gap-2 mb-1.5">
            <div className="w-6 h-6 bg-teal-100 rounded-full flex items-center justify-center">
              <span className="text-xs font-bold text-teal-700">
                {business.reviews[0].author[0]}
              </span>
            </div>
            <span className="text-xs font-semibold text-gray-700">
              {business.reviews[0].author}
            </span>
            <span className="text-xs text-gray-400 ml-auto">{business.reviews[0].date}</span>
          </div>
          <p className="text-xs text-gray-600 leading-relaxed line-clamp-2">
            &ldquo;{business.reviews[0].text}&rdquo;
          </p>
        </div>

        <div className="mt-auto flex items-center gap-1.5 text-xs text-gray-500 mb-4">
          <i className="ri-map-pin-2-line text-teal-500" />
          <span className="truncate">{business.address}</span>
        </div>

        <a
          href={`tel:${business.phone}`}
          className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-teal-600 text-white rounded-lg text-sm font-semibold hover:bg-teal-700 transition-colors cursor-pointer whitespace-nowrap"
        >
          <i className="ri-phone-line" />
          Call Now
        </a>
      </div>
    </article>
  );
}

export default function LocalBusinessSpotlight() {
  const [activeCategory, setActiveCategory] = useState('all');

  // Merge sponsored businesses at the top
  const allBusinesses = [...homepageSpotlightBusinesses, ...spotlightBusinesses];

  const filtered =
    activeCategory === 'all'
      ? allBusinesses
      : allBusinesses.filter((b) => b.category === activeCategory);

  const featured = filtered.slice(0, 2);
  const grid = filtered.slice(2);

  return (
    <section className="py-24 bg-gradient-to-b from-stone-50 to-white">
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="text-center mb-14">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-amber-50 rounded-full mb-5">
            <i className="ri-store-2-line text-amber-600" />
            <span className="text-amber-700 text-sm font-semibold tracking-wide uppercase">
              Community Favorites
            </span>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Local Business <span className="text-teal-600">Spotlight</span>
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Discover the top-rated businesses that make Roseville a vibrant place to live, work,
            and play
          </p>
        </div>

        {/* Category Filter */}
        <div className="flex flex-wrap justify-center gap-2 mb-14">
          {spotlightCategories.map((cat) => (
            <button
              key={cat.id}
              onClick={() => setActiveCategory(cat.id)}
              className={`flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-medium transition-all duration-300 whitespace-nowrap cursor-pointer ${
                activeCategory === cat.id
                  ? 'bg-teal-600 text-white shadow-md shadow-teal-200'
                  : 'bg-white text-gray-600 hover:bg-teal-50 hover:text-teal-700 border border-gray-200'
              }`}
            >
              <i className={`${cat.icon} text-base`} />
              {cat.label}
            </button>
          ))}
        </div>

        {/* Featured Large Cards */}
        {featured.length > 0 && (
          <div className="space-y-8 mb-8">
            {featured.map((biz) => (
              <div key={biz.id} className="relative">
                {biz.isSponsored && (
                  <div className="absolute -top-3 right-6 z-10">
                    <span className="px-4 py-1.5 bg-gradient-to-r from-amber-500 to-orange-500 text-white text-xs font-bold rounded-full shadow-lg flex items-center gap-1.5 whitespace-nowrap">
                      <i className="ri-vip-crown-fill"></i>
                      SPONSORED SPOTLIGHT
                    </span>
                  </div>
                )}
                <BusinessCard business={biz} isLarge />
              </div>
            ))}
          </div>
        )}

        {/* Grid Cards */}
        {grid.length > 0 && (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-14">
            {grid.map((biz) => (
              <BusinessCard key={biz.id} business={biz} isLarge={false} />
            ))}
          </div>
        )}

        {filtered.length === 0 && (
          <div className="text-center py-16">
            <i className="ri-search-line text-5xl text-gray-300 mb-4 block" />
            <p className="text-gray-500 text-lg">No businesses found in this category yet.</p>
          </div>
        )}

        {/* Stats Row */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-14">
          {[
            { icon: 'ri-store-2-line', value: '200+', label: 'Local Businesses' },
            { icon: 'ri-star-line', value: '4.7', label: 'Avg. Rating' },
            { icon: 'ri-chat-smile-2-line', value: '12K+', label: 'Customer Reviews' },
            { icon: 'ri-map-pin-line', value: '50+', label: 'Neighborhoods Served' },
          ].map((stat, i) => (
            <div
              key={i}
              className="text-center p-5 bg-white rounded-xl border border-gray-100 shadow-sm"
            >
              <div className="w-10 h-10 flex items-center justify-center mx-auto mb-2 bg-teal-50 rounded-lg">
                <i className={`${stat.icon} text-xl text-teal-600`} />
              </div>
              <div className="text-2xl font-bold text-gray-900">{stat.value}</div>
              <div className="text-xs text-gray-500 mt-0.5">{stat.label}</div>
            </div>
          ))}
        </div>

        {/* CTA Banner */}
        <div className="relative rounded-2xl overflow-hidden bg-gradient-to-r from-teal-700 to-teal-900 p-10">
          <div className="absolute top-0 right-0 w-64 h-64 bg-teal-600/30 rounded-full -translate-y-1/2 translate-x-1/3" />
          <div className="absolute bottom-0 left-1/3 w-40 h-40 bg-amber-500/10 rounded-full translate-y-1/2" />
          <div className="relative flex flex-col md:flex-row items-center justify-between gap-8">
            <div className="text-white">
              <h3 className="text-2xl font-bold mb-2">Own a Business in Roseville?</h3>
              <p className="text-white/80 max-w-lg text-sm">
                Get featured in our Local Business Spotlight and reach thousands of residents and
                visitors every month. Sponsored placements start at just $200/week.
              </p>
            </div>
            <div className="flex gap-3">
              <Link
                to="/advertise"
                className="px-6 py-3 bg-white text-teal-800 rounded-lg font-semibold hover:bg-gray-100 transition-colors whitespace-nowrap cursor-pointer"
              >
                Get Featured
              </Link>
              <Link
                to="/directory"
                className="px-6 py-3 bg-white/10 border border-white/30 text-white rounded-lg font-semibold hover:bg-white/20 transition-colors whitespace-nowrap cursor-pointer"
              >
                Browse Directory
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
